import { coerceDateTime } from '../my-journeys/myJourneysModel';

export const COLLABORATION_TIMELINE_SOURCE_TYPES = Object.freeze(['DECISION', 'SITUATION']);

export const COLLABORATION_TIMELINE_EVENT_TYPES = Object.freeze([
  'DecisionCreatedIntegrationEvent',
  'DecisionVotingOpenedIntegrationEvent',
  'DecisionClosedIntegrationEvent',
  'DecisionCancelledIntegrationEvent',
  'DecisionDraftUpdatedIntegrationEvent',
  'DecisionVoteCastIntegrationEvent',
  'DecisionVoteChangedIntegrationEvent',
  'SituationCreatedIntegrationEvent',
  'SituationResolvedIntegrationEvent',
  'SituationCancelledIntegrationEvent',
  'SituationUpdatedIntegrationEvent',
  'SituationAssignedIntegrationEvent',
]);

const EVENT_LABELS = Object.freeze({
  DecisionCreatedIntegrationEvent: '決策已建立',
  DecisionVotingOpenedIntegrationEvent: '決策開始投票',
  DecisionClosedIntegrationEvent: '決策已結束',
  DecisionCancelledIntegrationEvent: '決策已取消',
  DecisionDraftUpdatedIntegrationEvent: '決策草稿已更新',
  DecisionVoteCastIntegrationEvent: '決策已投票',
  DecisionVoteChangedIntegrationEvent: '決策投票已變更',
  SituationCreatedIntegrationEvent: '新的旅程狀況',
  SituationResolvedIntegrationEvent: '狀況已解決',
  SituationCancelledIntegrationEvent: '狀況已取消',
  SituationUpdatedIntegrationEvent: '狀況已更新',
  SituationAssignedIntegrationEvent: '狀況已指派',
});

const SOURCE_LABELS = Object.freeze({
  DECISION: '決策',
  SITUATION: '旅程狀況',
});

const CTA_LABELS = Object.freeze({
  DECISION: '查看決策',
  SITUATION: '查看旅程狀況',
});

export function asIdString(value) {
  if (value == null || value === '') return '';
  return String(value);
}

export function isPositiveId(value) {
  return /^[1-9]\d*$/.test(asIdString(value));
}

export function collaborationTimelineEventLabel(timelineEventType) {
  const key = String(timelineEventType || '');
  if (EVENT_LABELS[key]) return EVENT_LABELS[key];
  return key ? `未知事件（${key}）` : '旅程動態';
}

export function collaborationTimelineSourceLabel(sourceType) {
  const key = String(sourceType || '');
  if (SOURCE_LABELS[key]) return SOURCE_LABELS[key];
  return key ? `未知來源（${key}）` : '';
}

export function collaborationTimelineCtaLabel(sourceType) {
  const key = String(sourceType || '');
  return CTA_LABELS[key] || '';
}

export function collaborationTimelineSourceRoute(item) {
  const journeyId = asIdString(item?.journeyId);
  const sourceId = asIdString(item?.sourceId);
  const sourceType = String(item?.sourceType || '');
  if (!isPositiveId(journeyId) || !isPositiveId(sourceId)) return '';
  if (sourceType === 'DECISION') return `/journeys/${journeyId}/decisions/${sourceId}`;
  if (sourceType === 'SITUATION') return `/journeys/${journeyId}/situations/${sourceId}`;
  return '';
}

export function unwrapCollaborationTimelinePage(payload) {
  if (Array.isArray(payload)) {
    return { items: payload, nextCursor: null, hasMore: false };
  }
  const items = Array.isArray(payload?.items) ? payload.items : [];
  return {
    items,
    nextCursor: payload?.nextCursor || null,
    hasMore: Boolean(payload?.hasMore),
  };
}

export function normalizeCollaborationTimelineCursor(value) {
  if (!value) return null;
  const sortTime = coerceDateTime(value.sortTime) || asIdString(value.sortTime);
  const timelineItemId = asIdString(value.timelineItemId);
  if (!sortTime || !isPositiveId(timelineItemId)) return null;
  return { sortTime, timelineItemId };
}

export function normalizeCollaborationTimelineItem(value) {
  const timelineItemId = asIdString(value?.timelineItemId);
  const journeyId = asIdString(value?.journeyId);
  const sourceId = asIdString(value?.sourceId);
  const sourceType = String(value?.sourceType || '');
  const timelineEventType = String(value?.timelineEventType || '');
  const actorMemberId = value?.actorMemberId == null || value?.actorMemberId === ''
    ? ''
    : asIdString(value.actorMemberId);
  const route = collaborationTimelineSourceRoute({ journeyId, sourceId, sourceType });
  const title = String(value?.title || '').trim() || collaborationTimelineEventLabel(timelineEventType);
  return {
    timelineItemId,
    journeyId,
    sourceType,
    sourceId,
    sourceRowVersion: asIdString(value?.sourceRowVersion),
    timelineEventType,
    projectionMode: String(value?.projectionMode || ''),
    title,
    summary: value?.summary == null ? '' : String(value.summary),
    status: value?.status == null ? '' : String(value.status),
    severity: value?.severity == null ? '' : String(value.severity),
    actorMemberId,
    occurredAt: coerceDateTime(value?.occurredAt) || asIdString(value?.occurredAt),
    sortTime: coerceDateTime(value?.sortTime) || asIdString(value?.sortTime),
    eventLabel: collaborationTimelineEventLabel(timelineEventType),
    sourceLabel: collaborationTimelineSourceLabel(sourceType),
    sourceRoute: route,
    ctaLabel: route ? collaborationTimelineCtaLabel(sourceType) : '',
  };
}

export function normalizeCollaborationTimelinePage(payload) {
  const page = unwrapCollaborationTimelinePage(payload);
  return {
    items: page.items.map(normalizeCollaborationTimelineItem).filter((item) => isPositiveId(item.timelineItemId)),
    nextCursor: normalizeCollaborationTimelineCursor(page.nextCursor),
    hasMore: Boolean(page.hasMore),
  };
}

export function appendCollaborationTimelineItems(current, incoming) {
  const seen = new Set((Array.isArray(current) ? current : []).map((item) => item.timelineItemId));
  const extra = (Array.isArray(incoming) ? incoming : []).filter((item) => !seen.has(item.timelineItemId));
  return [...(Array.isArray(current) ? current : []), ...extra];
}

export function classifyCollaborationTimelineError(cause) {
  const code = cause?.code ?? cause?.raw?.code;
  const key = String(cause?.raw?.key || cause?.message || '');
  if (code === 90003 || code === 90005 || code === 90006
      || key.includes('token.not.found') || key.includes('token.illegal') || key.includes('token.expired')) {
    return 'unauthorized';
  }
  if (key === 'travel.journey.not.found') return 'journey-not-found';
  if (code === 94001 || key === 'travel.collaboration.permission.denied') return 'forbidden';
  if (code === 94000 || key === 'travel.collaboration.integration.validation.failed') return 'validation';
  if (key.includes('not.found')) return 'not-found';
  return 'error';
}

export function collaborationTimelineErrorMessage(cause, fallback) {
  const kind = classifyCollaborationTimelineError(cause);
  if (kind === 'unauthorized') return '登入已過期，請重新登入。';
  if (kind === 'forbidden') return '你沒有權限查看這趟旅程動態。';
  if (kind === 'journey-not-found' || kind === 'not-found') return '找不到這趟旅程。';
  if (kind === 'validation') return '動態請求不正確。';
  return cause?.raw?.message || cause?.message || fallback || '旅程動態無法載入。';
}
